import { useState, useEffect, useRef } from "react";

import { useLocation, Link, useNavigate } from "react-router-dom";
import logo from "../../src/assets/images/logo.png";
import small from "../../src/assets/images/logo-small.png";

import creditCard from "../assets/images/credit cards-smll.png";
import dmvLogo from "../../src/assets/images/dmv-logo-dark.png";
import { ReactGoogleReviews } from "react-google-reviews";
import MobileMenuOverlay from "./MobileMenuOverlay";

const Header = ({ menus, settings }) => {
  const featurableWidgetId = import.meta.env.VITE_FEATURABLE_WIDGET_ID;
  const location = useLocation();
  const { navigate } = useNavigate();

  const [isSticky, setSticky] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState({});
  const headerRef = useRef(null);
  const navContainerRef = useRef(null);

  useEffect(() => {
    const navContainer = navContainerRef.current;
    if (!navContainer) return;

    const stickyPoint = navContainer.offsetTop;

    const handleScroll = () => {
      if (window.innerWidth > 991 && window.scrollY > stickyPoint) {
        setSticky(true);
      } else {
        setSticky(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenSubmenus({});
  }, [location.pathname]);

  // Close menu when resizing to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 991) {
        setMobileMenuOpen(false);
        setOpenSubmenus({});
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const county = [
    "/miami-dade",
    "/broward-county",
    "/palmbeach-county",
    "/central-florida",
    "/customer-support",
  ];

  const handleLinkClick = (e) => {
    e.currentTarget.blur();
    window.scrollTo({ top: 0, behavior: "instant" });
    setMobileMenuOpen(false);
    setOpenSubmenus({});
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    if (mobileMenuOpen) {
      setOpenSubmenus({});
    }
  };

  const toggleSubmenu = (menuId, level = 1) => {
    setOpenSubmenus((prev) => {
      const newState = { ...prev };
      const key = `${level}-${menuId}`;

      Object.keys(newState).forEach((k) => {
        if (k.startsWith(`${level}-`) && k !== key) {
          delete newState[k];
        }
      });

      if (newState[key]) {
        delete newState[key];
      } else {
        newState[key] = true;
      }

      return newState;
    });
  };

  const getActiveClass = (sData, location) => {
    let classes = "link-menu";
    if (location.substring(1) === sData.slug) {
      classes += `${sData && sData.sub_menu ? " has-sub " : " "} active`;
    } else {
      classes += `${sData && sData.sub_menu ? " has-sub" : ""}`;
    }
    return classes;
  };

  const checkMenuActive = (mData) => {
    if (!mData) return false;
    if (location.pathname === `/${mData.slug}` || (mData.slug === "/" && location.pathname === "/")) {
      return true;
    }

    if (mData.slug === "#" && county.includes(location.pathname)) {
      return true;
    }

    if (mData.sub_menu && Array.isArray(mData.sub_menu)) {
      return mData.sub_menu.some(checkMenuActive);
    }
    return false;
  };

  const activeMenuIndex = Array.isArray(menus) ? menus.findIndex(checkMenuActive) : -1;

  const getHeadActiveClass = (mData, index) => {
    let classes = "link-menu";
    if (index === activeMenuIndex) classes += " active";
    if (mData.sub_menu?.length) classes += " has-sub";
    return classes;
  };


  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
      document.documentElement.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
      document.documentElement.style.overflow = "unset";
    };
  }, [mobileMenuOpen]);

  return (
    <>
      <header
        ref={headerRef}
        className={`notranslate bg-light-grey header-section ${isSticky ? "header-sticky" : ""
          }`}
      >
        <div 
          className="max-width-container row align-items-center"
          style={{ position: "relative", zIndex: 10001 }}
        >
          <div className="col-lg-4 d-lg-flex d-none align-items-end justify-content-start gap-3">
            {settings && (
              <Link to={"/"}>
                <img
                  src={logo}
                  alt="Metro Traffic School Logo"
                  style={{ maxWidth: "100%", height: "auto" }}
                />
              </Link>
            )}
          </div>
          <div className="col-12 col-lg-8 position-relative">
            <div className="d-flex align-items-center justify-content-lg-end justify-content-between">
              <Link to={"/"}>
                <img src={small} alt="hi" className="d-lg-none d-inline" />
              </Link>
              
              <div
                className={`button d-lg-none ${mobileMenuOpen ? "menu-opened" : ""}`}
                style={{ 
                  position: 'relative', 
                  top: 'auto', 
                  right: 'auto',
                  transform: 'scale(0.8)',
                  marginLeft: 'auto'
                }}
                onClick={toggleMobileMenu}
              ></div>

              <div className="login-links d-flex align-items-center">
                <div className="d-none d-lg-flex align-items-center gap-3 me-3 header-badges">
                  <img src={dmvLogo} alt="dmvlogo" />

                  <div
                    style={{
                      maxWidth: "120px",
                      transform: "scale(0.6)",
                      transformOrigin: "left",
                    }}
                  >
                    <ReactGoogleReviews
                      theme="dark"
                      layout="badge"
                      featurableId={featurableWidgetId}
                    />
                  </div>

                  <div className="d-flex flex-column align-items-center ms-1">
                    <p
                      className="text-capitalize text-white mb-1"
                      style={{ fontSize: "0.9rem" }}
                    >
                      Payment methods we accept
                    </p>
                    <div className="card-logo d-flex align-items-center">
                      <img
                        src={creditCard}
                        alt="credit"
                        style={{ height: "30px" }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div ref={navContainerRef} className="nav-container d-none d-lg-block">
          <div className="row align-items-center">
            <div className="menu d-flex d-md-flex justify-content-center col-12">
              <nav id="cssmenu">
                <ul
                  className="d-none d-lg-flex flex-lg-wrap justify-content-lg-center"
                >
                  {menus &&
                    menus.length &&
                    menus.map((mData, index) => {
                      if (mData.name !== "FAQ" && mData.id !== 14) {
                        const hasSubmenu = mData.sub_menu && mData.sub_menu.length > 0;
                        const submenuKey = `1-${index}`;
                        const isSubmenuOpen = openSubmenus[submenuKey];

                        return (
                          <li
                            key={index}
                            className={getHeadActiveClass(mData, index)}
                          >
                            {mData.type === "url" ? (
                              <a
                                href={mData.external_url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {mData.name}
                              </a>
                            ) : (
                              <Link
                                to={
                                  mData.slug === "/"
                                    ? `${mData.slug}`
                                    : `/${mData.slug}`
                                }
                                onClick={handleLinkClick}
                              >
                                {mData.name}
                              </Link>
                            )}
                            {hasSubmenu && (
                              <>
                                <span
                                  className={`submenu-button ${isSubmenuOpen ? "submenu-opened" : ""}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    toggleSubmenu(index, 1);
                                  }}
                                ></span>
                                <ul
                                  className={`sub-level-menu ${isSubmenuOpen ? "open" : ""}`}
                                  style={{ display: isSubmenuOpen || window.innerWidth > 991 ? "block" : "none" }}
                                >
                                  {mData.sub_menu.map((sData, sIndex) => {
                                    const hasThirdLevel = sData.sub_menu && sData.sub_menu.length > 0;
                                    const thirdLevelKey = `2-${index}-${sIndex}`;
                                    const isThirdLevelOpen = openSubmenus[thirdLevelKey];

                                    return (
                                      <li
                                        key={sIndex}
                                        className={getActiveClass(
                                          sData,
                                          location.pathname
                                        )}
                                      >
                                        {sData.type === "url" ? (
                                          <a
                                            href={sData.external_url}
                                            target="_blank"
                                            rel="noreferrer"
                                          >
                                            {sData.name}
                                          </a>
                                        ) : sData.slug === "#" ? (
                                          <a
                                            href="#"
                                            onClick={(e) => e.preventDefault()}
                                            style={{ cursor: "default" }}
                                          >
                                            {sData.name}
                                          </a>
                                        ) : (
                                          <Link
                                            key={sIndex}
                                            to={`/${sData.slug}`}
                                            onClick={handleLinkClick}
                                          >
                                            {sData.name}
                                          </Link>
                                        )}
                                        {hasThirdLevel && (
                                          <>
                                            <span
                                              className={`submenu-button ${isThirdLevelOpen ? "submenu-opened" : ""}`}
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                toggleSubmenu(`${index}-${sIndex}`, 2);
                                              }}
                                            ></span>
                                            <ul
                                              className={`third-level-menu ${isThirdLevelOpen ? "open" : ""}`}
                                              style={{ display: isThirdLevelOpen || window.innerWidth > 991 ? "block" : "none" }}
                                            >
                                              {sData.sub_menu.map(
                                                (ssData, ssIndex) => {
                                                  return (
                                                    <li
                                                      key={ssIndex}
                                                      className={getActiveClass(
                                                        ssData,
                                                        location.pathname
                                                      )}
                                                    >
                                                      <Link
                                                        to={`/${ssData.slug}`}
                                                        onClick={handleLinkClick}
                                                      >
                                                        {ssData.name}
                                                      </Link>
                                                    </li>
                                                  );
                                                }
                                              )}
                                            </ul>
                                          </>
                                        )}
                                      </li>
                                    );
                                  })}
                                </ul>
                              </>
                            )}
                          </li>
                        );
                      }
                    })}
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </header>
      <MobileMenuOverlay 
        isOpen={mobileMenuOpen} 
        onClose={toggleMobileMenu} 
        menus={menus} 
      />
    </>
  );
};

export default Header;
